/**
 * TailAdmin TABLO + ROZET bileşenleri (MIT) — light/dark uyumlu, sade API.
 * Mevcut shadcn `table.tsx` bozulmadan yanında yaşar; yeni/yenilenen ekranlar
 * bunu kullanır: <GlTable><GlTHead>…<GlTh/></GlTHead><GlTBody><GlTr><GlTd/>…
 */
import type { ReactNode } from "react";

export function GlTableWrap({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03] ${className}`}>
      <div className="max-w-full overflow-x-auto">{children}</div>
    </div>
  );
}

export function GlTable({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <table className={`min-w-full ${className}`}>{children}</table>;
}

export function GlTHead({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <thead className={`border-b border-gray-100 dark:border-gray-800 ${className}`}>{children}</thead>;
}

export function GlTBody({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <tbody className={`divide-y divide-gray-100 dark:divide-gray-800 ${className}`}>{children}</tbody>;
}

export function GlTr({ children, className = "", onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  return (
    <tr onClick={onClick} className={`${onClick ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.03] " : ""}${className}`}>
      {children}
    </tr>
  );
}

export function GlTh({ children, className = "", align = "left" }: { children?: ReactNode; className?: string; align?: "left" | "right" | "center" }) {
  return (
    <th className={`px-5 py-3 text-${align} text-theme-xs font-medium text-gray-500 dark:text-gray-400 ${className}`}>
      {children}
    </th>
  );
}

export function GlTd({ children, className = "", align = "left", colSpan }: { children?: ReactNode; className?: string; align?: "left" | "right" | "center"; colSpan?: number }) {
  return (
    <td colSpan={colSpan} className={`px-5 py-4 text-${align} text-theme-sm text-gray-700 dark:text-gray-300 ${className}`}>
      {children}
    </td>
  );
}

// ── ROZET (TailAdmin Badge) ────────────────────────────────────────────────────
type BadgeColor = "primary" | "success" | "error" | "warning" | "info" | "light" | "dark";
const BADGE_LIGHT: Record<BadgeColor, string> = {
  primary: "bg-brand-50 text-brand-500 dark:bg-brand-500/15 dark:text-brand-400",
  success: "bg-success-50 text-success-600 dark:bg-success-500/15 dark:text-success-500",
  error: "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-500",
  warning: "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400",
  info: "bg-blue-light-50 text-blue-light-500 dark:bg-blue-light-500/15 dark:text-blue-light-500",
  light: "bg-gray-100 text-gray-700 dark:bg-white/5 dark:text-white/80",
  dark: "bg-gray-500 text-white dark:bg-white/5 dark:text-white",
};
const BADGE_SOLID: Record<BadgeColor, string> = {
  primary: "bg-brand-500 text-white",
  success: "bg-success-500 text-white",
  error: "bg-error-500 text-white",
  warning: "bg-warning-500 text-white",
  info: "bg-blue-light-500 text-white",
  light: "bg-gray-400 text-white",
  dark: "bg-gray-700 text-white",
};

export function GlBadge({
  children, color = "primary", variant = "light", size = "sm", className = "",
}: {
  children: ReactNode; color?: BadgeColor; variant?: "light" | "solid"; size?: "sm" | "md"; className?: string;
}) {
  const palette = variant === "solid" ? BADGE_SOLID : BADGE_LIGHT;
  return (
    <span className={`inline-flex items-center justify-center gap-1 rounded-full px-2.5 py-0.5 font-medium ${size === "sm" ? "text-theme-xs" : "text-theme-sm"} ${palette[color]} ${className}`}>
      {children}
    </span>
  );
}
